import React, { Component } from 'react'
import { func, string } from 'prop-types'
import { translate } from 'react-i18next'
import { stores } from '../../../stores'
import { PostalCodePicker } from '../../gardeners/PostalCodePicker'
import { isPostalCode } from '../../../utilities/validators'

class NoGardenersComponent extends Component {
  constructor (props) {
    super(props)
    this.state = {
      postalCodeInput: '',
      isLoading: false,
      isGeolocating: false,
      error: null,
      geolocatedCity: null
    }
  }

  componentDidMount () {
    // Auto-trigger geolocation when arriving via pro_apport
    const hasProReferral = !!stores.search.search.proBusinessReferral
    const { postalCode, referralCode } = this.props
    const hasNoSearch = !postalCode && !referralCode

    if (hasProReferral && hasNoSearch && this.isGeolocationAvailable()) {
      this.handleGeolocation()
    }
  }

  isGeolocationAvailable = () => {
    return 'geolocation' in navigator
  }

  handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5)
    this.setState({ postalCodeInput: value, error: null, geolocatedCity: null })
  }

  handleGeolocation = async () => {
    if (!this.isGeolocationAvailable()) {
      this.setState({ error: this.props.t('common:jardiniers:pro_referral_geoloc_unavailable') })
      return
    }

    this.setState({ isGeolocating: true, error: null })

    try {
      const position = await this.getCurrentPosition()
      const { latitude, longitude } = position.coords

      // Reverse geocoding via geo.api.gouv.fr
      const response = await fetch(
        `https://geo.api.gouv.fr/communes?lat=${latitude}&lon=${longitude}&fields=nom,codesPostaux&format=json`
      )
      const data = await response.json()

      if (data && data.length > 0 && data[0].codesPostaux && data[0].codesPostaux.length > 0) {
        const postalCode = data[0].codesPostaux[0]
        const cityName = data[0].nom

        this.setState({
          postalCodeInput: postalCode,
          geolocatedCity: cityName,
          isGeolocating: false
        })
      } else {
        this.setState({
          error: this.props.t('common:jardiniers:pro_referral_geoloc_not_found'),
          isGeolocating: false
        })
      }
    } catch (err) {
      let errorMessage = this.props.t('common:jardiniers:pro_referral_geoloc_error')

      if (err.code === 1) {
        // Permission denied
        errorMessage = this.props.t('common:jardiniers:pro_referral_geoloc_denied')
      } else if (err.code === 2) {
        // Position unavailable
        errorMessage = this.props.t('common:jardiniers:pro_referral_geoloc_unavailable')
      } else if (err.code === 3) {
        // Timeout
        errorMessage = this.props.t('common:jardiniers:pro_referral_geoloc_timeout')
      }

      this.setState({ error: errorMessage, isGeolocating: false })
    }
  }

  getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      })
    })
  }

  handleSubmit = async (e) => {
    e.preventDefault()
    const { postalCodeInput } = this.state
    const { onPostalCodeChange } = this.props

    if (!isPostalCode(postalCodeInput)) {
      this.setState({ error: this.props.t('common:jardiniers:pro_referral_invalid_postal_code') })
      return
    }

    this.setState({ isLoading: true, error: null })

    try {
      if (onPostalCodeChange) {
        await onPostalCodeChange(postalCodeInput)
      }
    } catch (err) {
      this.setState({ error: this.props.t('common:jardiniers:pro_referral_search_error') })
    } finally {
      this.setState({ isLoading: false })
    }
  }

  renderProReferralForm () {
    const { t } = this.props
    const { postalCodeInput, isLoading, isGeolocating, error, geolocatedCity } = this.state
    const canSubmit = postalCodeInput.length === 5 && !isLoading && !isGeolocating

    return (
      <div className='pro-referral-landing'>
        <div className='pro-referral-landing__card'>
          {/* Header with icon */}
          <div className='pro-referral-landing__header'>
            <div className='pro-referral-landing__icon'>
              <svg width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z' />
                <circle cx='12' cy='10' r='3' />
              </svg>
            </div>
            <h1 className='pro-referral-landing__title'>
              {t('common:jardiniers:pro_referral_title')}
            </h1>
            <p className='pro-referral-landing__subtitle'>
              {t('common:jardiniers:pro_referral_subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={this.handleSubmit} className='pro-referral-landing__form'>
            {/* Geolocation button */}
            {this.isGeolocationAvailable() && (
              <button
                type='button'
                className='pro-referral-landing__geoloc-btn'
                onClick={this.handleGeolocation}
                disabled={isGeolocating || isLoading}
              >
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <circle cx='12' cy='12' r='10' />
                  <circle cx='12' cy='12' r='3' />
                  <line x1='12' y1='2' x2='12' y2='4' />
                  <line x1='12' y1='20' x2='12' y2='22' />
                  <line x1='2' y1='12' x2='4' y2='12' />
                  <line x1='20' y1='12' x2='22' y2='12' />
                </svg>
                {isGeolocating
                  ? t('common:jardiniers:pro_referral_geolocating')
                  : t('common:jardiniers:pro_referral_use_location')}
              </button>
            )}

            {/* Separator */}
            <div className='pro-referral-landing__separator'>
              <span>{t('common:jardiniers:pro_referral_or')}</span>
            </div>

            {/* Postal code input */}
            <div className='pro-referral-landing__input-wrapper'>
              <input
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                className={'pro-referral-landing__input' + (error ? ' pro-referral-landing__input--error' : '')}
                placeholder={t('common:jardiniers:pro_referral_placeholder')}
                value={postalCodeInput}
                onChange={this.handleInputChange}
                maxLength={5}
                disabled={isLoading || isGeolocating}
                autoFocus
              />
              {geolocatedCity && (
                <span className='pro-referral-landing__city-hint'>
                  {geolocatedCity}
                </span>
              )}
            </div>

            {/* Error message */}
            {error && (
              <p className='pro-referral-landing__error'>{error}</p>
            )}

            {/* Submit button */}
            <button
              type='submit'
              className='pro-referral-landing__submit-btn'
              disabled={!canSubmit}
            >
              {isLoading && <span className='pro-referral-landing__spinner' />}
              <span>{isLoading ? t('common:jardiniers:pro_referral_loading') : t('common:jardiniers:pro_referral_submit')}</span>
            </button>
          </form>

        
        </div>
      </div>
    )
  }

  renderDefaultState () {
    const { t, postalCode, referralCode, onPostalCodeChange } = this.props
    const none = !postalCode && !referralCode

    let firstText = t('common:jardiniers:empty_message_nothing')
    let secondText = 'common:jardiniers:empty_message_call_us'

    if (!none) {
      firstText = postalCode
        ? `${t('common:jardiniers:empty_message_no_postal_code')} : ${postalCode}`
        : `${t('common:jardiniers:empty_message_no_referral_code')} : ${referralCode}`
      secondText = postalCode
        ? 'common:jardiniers:empty_message_second'
        : 'common:jardiniers:empty_message_five'
    }

    return (
      <div className='no-gardeners__container'>
        <img
          alt='rateau illustration'
          className='img--centered img--rounded'
          src='/app/images/rateau.png' />
        {
          stores.search.search.allPostalCodesOfCity && stores.search.search.allPostalCodesOfCity.length > 1 &&
          <PostalCodePicker
            postalCodes={stores.search.search.allPostalCodesOfCity}
            selected={postalCode || stores.search.search.postalCode}
            onChange={onPostalCodeChange}
          />
        }
        <p className='paragraph--centered'>
          {`${firstText}.`}
        </p>
        <p className='paragraph--centered'>
          {`${t(secondText)}.`}
        </p>
        <p className='paragraph--centered'>
          {t('common:jardiniers:empty_message_third')}.
        </p>
      </div>
    )
  }

  render () {
    const { postalCode, referralCode } = this.props
    const hasProReferral = !!stores.search.search.proBusinessReferral
    const hasNoSearch = !postalCode && !referralCode

    // Show postal code input form when arriving via pro_apport link without postal code
    if (hasProReferral && hasNoSearch) {
      return this.renderProReferralForm()
    }

    return this.renderDefaultState()
  }
}

NoGardenersComponent.propTypes = {
  postalCode: string,
  referralCode: string,
  t: func,
  onPostalCodeChange: func
}

export const NoGardeners = translate()(NoGardenersComponent)



// WEBPACK FOOTER //
// ./src/components/ui/states/NoGardeners.js